import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryParam } from '../../../../../utils/hooks/useQueryParam';
import { withViewerAsset } from './viewerAssetSearch';

export interface ViewerAssetUrl {
  /** `?asset=` of the page; '' when no viewer is open. */
  urlAsset: string;
  /** A click opened the viewer: a new history entry, so Back closes the viewer. */
  show: (uuid: string, revision?: number) => void;
  /** The viewer moved to another item: the entry changes, no new one. */
  replace: (uuid: string) => void;
  /**
   * The viewer closed: the value goes from the address with no new entry. Not `navigate(-1)`: a
   * close that opens another page at once ("Post it" opens the Composer) would race the async Back.
   */
  clear: () => void;
  /** Drops a link that names no asset of this release (no new history entry). */
  drop: () => void;
}

/**
 * Keeps the open asset of the release page viewer in the address (`?asset=&rev=`), so a refresh
 * opens the same asset again and a share link or an email link lands with the viewer open.
 */
export function useViewerAssetUrl(): ViewerAssetUrl {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const urlAsset = useQueryParam('asset', undefined, '');
  const go = useCallback(
    (nextSearch: string, replace: boolean) => {
      if (nextSearch !== search) {
        // eslint-disable-next-line no-void
        void navigate({ pathname, search: nextSearch }, { replace });
      }
    },
    [navigate, pathname, search]
  );

  const show = useCallback(
    (uuid: string, revision?: number) => {
      go(withViewerAsset(search, uuid, revision), false);
    },
    [go, search]
  );
  const replace = useCallback((uuid: string) => go(withViewerAsset(search, uuid), true), [go, search]);
  const clear = useCallback(() => go(withViewerAsset(search, null), true), [go, search]);

  return { urlAsset, show, replace, clear, drop: clear };
}
